import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Building2, Mail, Phone, MapPin, User, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function PharmaciesManagement({ currentUser }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    admin_username: '',
    admin_password: '',
    admin_full_name: '',
    admin_email: '',
  });

  useEffect(() => {
    fetchPharmacies();
  }, []);

  const fetchPharmacies = async () => {
    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      const response = await axios.get(`${API}/pharmacies`, { headers });
      setPharmacies(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des pharmacies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (formData.admin_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      await axios.post(`${API}/pharmacies`, formData, { headers });
      
      toast.success('Pharmacie créée avec succès!');
      setDialogOpen(false);
      setFormData({
        name: '',
        address: '',
        phone: '',
        email: '',
        admin_username: '',
        admin_password: '',
        admin_full_name: '',
        admin_email: '',
      });
      fetchPharmacies();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de la création de la pharmacie";
      toast.error(errorMessage);
    }
  };

  const togglePharmacyStatus = async (pharmacyId, currentStatus) => {
    try {
      const headers = { 'X-User-Id': currentUser?.id || '' };
      
      await axios.patch(
        `${API}/pharmacies/${pharmacyId}/status`,
        { is_active: !currentStatus },
        { headers }
      );
      
      toast.success('Statut mis à jour avec succès');
      fetchPharmacies();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Check if current user is super admin (no pharmacy_id)
  const isSuperAdmin = !currentUser?.pharmacy_id;

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Accès Refusé</CardTitle>
            <CardDescription>
              Seul l'administrateur principal peut gérer les pharmacies.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="pharmacies-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Pharmacies</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">{pharmacies.length} pharmacie(s) enregistrée(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-700 hover:to-emerald-700" data-testid="add-pharmacy-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Pharmacie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pharmacy-dialog">
            <DialogHeader>
              <DialogTitle>Créer une Nouvelle Pharmacie</DialogTitle>
              <DialogDescription>
                Créez une pharmacie avec son administrateur dédié
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pharmacy Information */}
              <div className="border-b pb-4">
                <h4 className="font-semibold mb-3 flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Informations Pharmacie
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name">Nom de la Pharmacie *</Label>
                    <Input
                      id="name"
                      data-testid="pharmacy-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="Ex: Pharmacie Centrale"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Adresse</Label>
                    <Input
                      id="address"
                      data-testid="pharmacy-address-input"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-1"
                      placeholder="123 Rue de la Pharmacie"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      data-testid="pharmacy-phone-input"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="mt-1"
                      placeholder="+213 555 123 456"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="pharmacy-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="mt-1"
                      placeholder="contact@pharmacie.dz"
                    />
                  </div>
                </div>
              </div>

              {/* Admin Information */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Administrateur de la Pharmacie
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="admin_full_name">Nom Complet *</Label>
                    <Input
                      id="admin_full_name"
                      data-testid="admin-fullname-input"
                      value={formData.admin_full_name}
                      onChange={(e) => setFormData({ ...formData, admin_full_name: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="Dr. Ahmed Benali"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_username">Nom d'Utilisateur *</Label>
                    <Input
                      id="admin_username"
                      data-testid="admin-username-input"
                      value={formData.admin_username}
                      onChange={(e) => setFormData({ ...formData, admin_username: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="admin_pharmacie1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin_password">Mot de Passe *</Label>
                    <Input
                      id="admin_password"
                      data-testid="admin-password-input"
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                      required
                      minLength={6}
                      className="mt-1"
                      placeholder="Min. 6 caractères"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="admin_email">Email</Label>
                    <Input
                      id="admin_email"
                      data-testid="admin-email-input"
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                      className="mt-1"
                      placeholder="admin@pharmacie.dz"
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="submit-pharmacy-btn" 
                className="w-full bg-gradient-to-r from-[#004d3d] to-[#00ff7f] hover:from-green-700 hover:to-emerald-700"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Créer la Pharmacie
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pharmacies Table */}
      <Card data-testid="pharmacies-table">
        <CardHeader>
          <CardTitle>Liste des Pharmacies</CardTitle>
          <CardDescription>Gérez toutes les pharmacies enregistrées</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : pharmacies.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune pharmacie enregistrée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Administrateur</TableHead>
                    <TableHead>Date Création</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pharmacies.map((pharmacy) => (
                    <TableRow key={pharmacy.id} data-testid="pharmacy-row">
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <Building2 className="w-4 h-4 mr-2 text-green-600" />
                          {pharmacy.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm text-gray-600">
                          {pharmacy.address ? (
                            <>
                              <MapPin className="w-3 h-3 mr-1" />
                              {pharmacy.address}
                            </>
                          ) : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          {pharmacy.phone && (
                            <div className="flex items-center text-gray-600">
                              <Phone className="w-3 h-3 mr-1" />
                              {pharmacy.phone}
                            </div>
                          )}
                          {pharmacy.email && (
                            <div className="flex items-center text-gray-600">
                              <Mail className="w-3 h-3 mr-1" />
                              {pharmacy.email}
                            </div>
                          )}
                          {!pharmacy.phone && !pharmacy.email && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center text-sm">
                          <User className="w-3 h-3 mr-1 text-blue-600" />
                          {pharmacy.admin_user_id || '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {format(new Date(pharmacy.created_at), 'dd/MM/yyyy', { locale: fr })}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={pharmacy.is_active ? 'default' : 'secondary'}
                          className={pharmacy.is_active ? 'bg-green-500' : 'bg-gray-400'}
                        >
                          {pharmacy.is_active ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => togglePharmacyStatus(pharmacy.id, pharmacy.is_active)}
                          data-testid="toggle-status-btn"
                        >
                          {pharmacy.is_active ? 'Désactiver' : 'Activer'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PharmaciesManagement;
